import { useCallback, useRef, useState, useMemo } from 'react';
import { IFileItem } from '../types';
import { downloadBudgetAttachment } from '../../../../api/fetchBudget';
import moment from 'moment';

interface IBudgetFileUploadProps {
  files?: IFileItem[];
  budgetId?: string;
  onFileClick?: (file: IFileItem, index: number) => void;
  onFileRemove?: (index: number) => void;
  onFilesAdd?: (files: IFileItem[]) => void;
}

const MAX_FILES = 8;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/csv',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
];

const ALLOWED_EXTENSIONS = [
  '.pdf',
  '.xls',
  '.xlsx',
  '.ods',
  '.csv',
  '.jpeg',
  '.jpg',
  '.png',
  '.gif',
  '.webp',
];

const BudgetFileUpload = (props: IBudgetFileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const files = props.files || [];

  const formatFileSize = (size?: string | number): string => {
    if (!size) return 'Tamanho não disponível';
    if (typeof size === 'string') {
      const sizeNum = Number.parseFloat(size);
      if (Number.isNaN(sizeNum)) return size;
      return formatBytes(sizeNum);
    }
    return formatBytes(size);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const isValidFileType = (file: File): boolean => {
    const fileType = file.type?.toLowerCase().trim() || '';
    const fileName = file.name.toLowerCase();
    const lastDotIndex = fileName.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex).toLowerCase() : '';

    if (fileType && ALLOWED_FILE_TYPES.some(allowed => fileType === allowed)) {
      return true;
    }

    if (extension && ALLOWED_EXTENSIONS.some(allowed => extension === allowed.toLowerCase())) {
      return true;
    }

    return false;
  };

  const getFileThumbnail = (file: IFileItem): string => {
    const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || '';
    
    if (fileType.startsWith('image/')) {
      if (file.link) {
        return file.link;
      }
      if (file.file) {
        return URL.createObjectURL(file.file);
      }
    }
    
    if (!fileType) return '/assets/images/file-icon.svg';
    
    if (fileType === 'application/pdf') {
      return '/assets/images/pdf-icon.svg';
    } else if (fileType.startsWith('video/')) {
      return '/assets/images/video-icon.svg';
    } else if (
      fileType === 'application/zip' ||
      fileType === 'application/x-rar-compressed'
    ) {
      return '/assets/images/zip-icon.svg';
    } else {
      return '/assets/images/file-icon.svg';
    }
  };

  const getFilePreviewUrl = useCallback((file: IFileItem): string | null => {
    const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || '';
    
    if (fileType === 'application/pdf') {
      if (file.link) {
        return file.link;
      }
      if (file.file) {
        return URL.createObjectURL(file.file);
      }
    }
    
    return null;
  }, []);

  const isPdfFile = (file: IFileItem): boolean => {
    const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || '';
    return fileType === 'application/pdf';
  };

  const isSpreadsheetFile = (file: IFileItem): boolean => {
    const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || '';
    return (
      fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileType === 'application/vnd.oasis.opendocument.spreadsheet' ||
      fileType === 'text/csv' ||
      file.name?.toLowerCase().endsWith('.xls') ||
      file.name?.toLowerCase().endsWith('.xlsx') ||
      file.name?.toLowerCase().endsWith('.ods') ||
      file.name?.toLowerCase().endsWith('.csv')
    );
  };

  const handleDownload = useCallback(
    async (file: IFileItem, index: number) => {
      if (file.link) {
        const link = document.createElement('a');
        link.href = file.link;
        link.setAttribute('download', file.name || '');
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        return;
      }

      if (file.file) {
        const url = URL.createObjectURL(file.file);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name || '');
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        URL.revokeObjectURL(url);
        return;
      }

      if (props.budgetId && file.name) {
        try {
          const response = await downloadBudgetAttachment(props.budgetId, file.name);
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', file.name);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('[BudgetFileUpload] Erro ao baixar arquivo:', error);
        }
      }
    },
    [props.budgetId],
  );


  const handleFileSelect = useCallback(
    (selectedFiles: FileList | null) => {
      if (!selectedFiles || selectedFiles.length === 0) return;

      setErrorMessage('');

      const currentFilesCount = files.length;
      const selectedFilesArray = Array.from(selectedFiles);
      const totalFiles = currentFilesCount + selectedFilesArray.length;

      if (totalFiles > MAX_FILES) {
        setErrorMessage(
          `Máximo de ${MAX_FILES} arquivos permitidos. Você tentou adicionar ${totalFiles} arquivos.`,
        );
        return;
      }

      const filesTooLarge: string[] = [];
      const invalidFileTypes: string[] = [];
      const validFiles: IFileItem[] = [];

      for (const file of selectedFilesArray) {
        if (!isValidFileType(file)) {
          invalidFileTypes.push(file.name);
          continue;
        }

        if (file.size > MAX_FILE_SIZE) {
          filesTooLarge.push(file.name);
        } else {
          validFiles.push({
            name: file.name,
            type: file.type,
            size: file.size,
            file,
          });
        }
      }

      if (invalidFileTypes.length > 0) {
        const fileNames = invalidFileTypes.join(', ');
        setErrorMessage(
          `Os seguintes arquivos têm tipos não permitidos: ${fileNames}. Tipos aceitos: PDF, planilhas (Excel, CSV) e imagens (JPEG, PNG, GIF, WebP).`,
        );
      } else if (filesTooLarge.length > 0) {
        const fileNames = filesTooLarge.join(', ');
        setErrorMessage(
          `Os seguintes arquivos excedem o limite de 10MB: ${fileNames}. Tamanho máximo permitido: 10MB por arquivo.`,
        );
      }

      if (validFiles.length > 0 && props.onFilesAdd) {
        const remainingSlots = MAX_FILES - currentFilesCount;
        const filesToAdd = validFiles.slice(0, remainingSlots);
        props.onFilesAdd(filesToAdd);

        if (validFiles.length > remainingSlots) {
          setErrorMessage(
            `Apenas ${remainingSlots} arquivo(s) foram adicionados. Limite máximo de ${MAX_FILES} arquivos atingido.`,
          );
        }
      }
    },
    [props, files],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileSelect(e.target.files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFileSelect],
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = e.dataTransfer.files;
      handleFileSelect(droppedFiles);
    },
    [handleFileSelect],
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (props.onFileRemove) {
        props.onFileRemove(index);
      }
    },
    [props],
  );

  return (
    <div className="mb-6 border border-gray-200 rounded-md p-4 m-6">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        accept=".pdf,.xls,.xlsx,.ods,.csv,.jpeg,.jpg,.png,.gif,.webp,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,text/csv,image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleInputChange}
      />
      <div>Upload de arquivos</div>
      {errorMessage && (
        <div className="mt-2 p-2 bg-red-100 border border-red-300 text-red-700 rounded text-size-12">
          {errorMessage}
        </div>
      )}
      <div className="flex flex-col gap-4 mt-4">
        <section
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          aria-label="Área de upload de arquivos"
          className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-md hover:border-gray-400 transition-colors"
        >
          <button
            type="button"
            className="cursor-pointer bg-transparent border-none p-0 flex flex-col items-center gap-2"
            onClick={handleUploadClick}
          >
            <img
              src="/assets/images/uploadFiles.svg"
              alt="Enviar arquivos"
              className="w-10 h-10"
            />
            <span className="text-size-14 text-gray-600">Clique ou arraste arquivos aqui</span>
            <span className="text-size-12 text-gray-500">Tipos aceitos: PDF, planilhas (Excel, CSV) e imagens (JPEG, PNG, GIF, WebP). Tamanho máximo: 10MB por arquivo.</span>
          </button>
        </section>

        {files.length > 0 && (
          <div className="flex flex-col gap-4">
            {files.map((file, index) => (
              <div
                key={`file-${file.name}-${index}-${file.size || ''}`}
                className="border border-gray-200 rounded-md p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex">
                  <div className="thumbnail mr-4 flex justify-center items-center w-[200px] h-[120px] border border-gray-200 rounded bg-gray-50 flex-shrink-0 relative overflow-hidden">
                    {isPdfFile(file) && getFilePreviewUrl(file) ? (
                      <div className="w-full h-full relative">
                        <iframe
                          src={`${getFilePreviewUrl(file)}#page=1&zoom=page-fit`}
                          className="w-full h-full border-0"
                          title={`Preview ${file.name}`}
                          style={{ pointerEvents: 'none' }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-size-10 px-2 py-1 flex items-center justify-center">
                          <span className="mr-1">📄</span>
                          <span>PDF</span>
                        </div>
                      </div>
                    ) : isPdfFile(file) ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-red-100 p-4">
                        <div className="text-5xl mb-2">📄</div>
                        <div className="text-size-12 text-gray-700 text-center font-semibold">PDF</div>
                        <div className="text-size-10 text-gray-600 text-center mt-1 truncate w-full">{file.name}</div>
                      </div>
                    ) : isSpreadsheetFile(file) ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
                        <div className="text-4xl mb-2">📊</div>
                        <div className="text-size-10 text-gray-600 text-center font-semibold">
                          {file.name?.toLowerCase().endsWith('.csv') ? 'CSV' : 'Planilha'}
                        </div>
                      </div>
                    ) : (
                      <img
                        src={getFileThumbnail(file)}
                        alt={file.name}
                        style={{
                          width: '200px',
                          height:
                            (file.type?.toLowerCase()?.includes('png') ||
                            file.type?.toLowerCase()?.includes('jpeg') ||
                            file.type?.toLowerCase()?.includes('jpg') ||
                            file.file?.type?.toLowerCase()?.includes('png') ||
                            file.file?.type?.toLowerCase()?.includes('jpeg') ||
                            file.file?.type?.toLowerCase()?.includes('jpg'))
                              ? '120px'
                              : '65px',
                          objectFit:
                            (file.type?.toLowerCase()?.includes('png') ||
                            file.type?.toLowerCase()?.includes('jpeg') ||
                            file.type?.toLowerCase()?.includes('jpg') ||
                            file.file?.type?.toLowerCase()?.includes('png') ||
                            file.file?.type?.toLowerCase()?.includes('jpeg') ||
                            file.file?.type?.toLowerCase()?.includes('jpg'))
                              ? 'cover'
                              : 'contain',
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/assets/images/file-icon.svg';
                        }}
                      />
                    )}
                  </div>
                  <div className="flex justify-between w-full">
                    <div className="flex-1">
                      <div className="font-bold mb-1">{file.name}</div>
                      {(file.type || file.file?.type) && (
                        <div className="text-size-12 text-gray-600">tipo: {file.type || file.file?.type}</div>
                      )}
                      {file.size && <div className="text-size-12 text-gray-600">tamanho: {formatFileSize(file.size)}</div>}
                      {file.updatedAt && (
                        <div className="text-size-12 text-gray-600">
                          data da última modificação: {moment(file.updatedAt).format('DD/MM/YYYY')}
                        </div>
                      )}
                      <div className="text-size-12 text-gray-600">caminho: {file.name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="cursor-pointer bg-transparent border-none p-2 hover:bg-gray-200 rounded transition-colors"
                        onClick={() => handleDownload(file, index)}
                        title="Download"
                      >
                        <img src="/assets/images/downloadFile.svg" alt="Download" className="w-6 h-6" />
                      </button>
                      <button
                        type="button"
                        className="cursor-pointer bg-transparent border-none p-2 hover:bg-gray-200 rounded transition-colors"
                        onClick={() => handleRemoveFile(index)}
                        title="Remover"
                      >
                        <img src="/assets/images/bin.svg" alt="Remover arquivo" className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetFileUpload;
